import AdminLink from "../components/AdminLink";
import { deleteAccount } from "../Services/accountService";
import React, {
  useEffect,
  useState,
} from "react";

import {
  onAuthStateChanged,
  signOut,
  updateProfile,
} from "firebase/auth";

import {
  doc,
  updateDoc,
} from "firebase/firestore";

import {
  LuCheck,
  LuLogOut,
  LuMail,
  LuPencil,
  LuSave,
  LuShieldAlert,
  LuTrash2,
  LuUser,
  LuX,
} from "react-icons/lu";

import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  auth,
  db,
} from "../firebase";

import Spinner from "../components/Spinner";

export default function Profile() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [loading, setLoading] =
    useState(true);

  const [saving, setSaving] =
    useState(false);

  const [deleting, setDeleting] =
    useState(false);

  const [
    changeDetail,
    setChangeDetail,
  ] = useState(false);

  const [
    showDeleteConfirm,
    setShowDeleteConfirm,
  ] = useState(false);

  const [formData, setFormData] =
    useState({
      name: "",
      email: "",
      reason: "",
    });

  /*
  |--------------------------------------------------------------------------
  | Charger l'utilisateur Firebase
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    const unsubscribe =
      onAuthStateChanged(
        auth,
        (user) => {
          if (!user) {
            setLoading(false);
            navigate("/SignIn");

            return;
          }

          setFormData({
            name:
              user.displayName ||
              "",
            email:
              user.email || "",
            reason: "",
          });

          setLoading(false);
        }
      );

    return unsubscribe;
  }, [navigate]);

  /*
  |--------------------------------------------------------------------------
  | Changement des champs
  |--------------------------------------------------------------------------
  */

  const onChange = (
    event
  ) => {
    const {
      id,
      value,
    } = event.target;

    setFormData(
      (previousValue) => ({
        ...previousValue,
        [id]: value,
      })
    );
  };

  /*
  |--------------------------------------------------------------------------
  | Déconnexion
  |--------------------------------------------------------------------------
  */

  const onLogout = async () => {
    try {
      await signOut(auth);

      navigate("/");
    } catch (error) {
      console.error(
        "Erreur déconnexion :",
        error
      );

      toast.error(
        t(
          "profile.logout_error",
          "Impossible de vous déconnecter."
        )
      );
    }
  };

  /*
  |--------------------------------------------------------------------------
  | Modifier le profil
  |--------------------------------------------------------------------------
  */

  const onSubmit =
    async () => {
      const user =
        auth.currentUser;

      if (!user) {
        return;
      }

      const normalizedName =
        formData.name.trim();

      if (!normalizedName) {
        toast.error(
          t(
            "profile.name_required",
            "Le nom ne peut pas être vide."
          )
        );

        return;
      }

      if (
        normalizedName ===
        (user.displayName || "")
      ) {
        setChangeDetail(false);

        toast.info(
          t(
            "profile.no_changes",
            "Aucune modification détectée."
          )
        );

        return;
      }

      setSaving(true);

      try {
        /*
          Mise à jour Firebase Auth
        */

        await updateProfile(
          user,
          {
            displayName:
              normalizedName,
          }
        );

        /*
          Mise à jour Firestore
        */

        const userRef = doc(
          db,
          "users",
          user.uid
        );

        await updateDoc(
          userRef,
          {
            name:
              normalizedName,
          }
        );

        setFormData(
          (previousValue) => ({
            ...previousValue,
            name:
              normalizedName,
          })
        );

        setChangeDetail(false);

        toast.success(
          t(
            "profile_updated",
            "Profil mis à jour."
          )
        );
      } catch (error) {
        console.error(
          "Erreur mise à jour profil :",
          error
        );

        toast.error(
          t(
            "profile_update_error",
            "Impossible de mettre à jour votre profil."
          )
        );
      } finally {
        setSaving(false);
      }
    };

  /*
  |--------------------------------------------------------------------------
  | Activer modification
  |--------------------------------------------------------------------------
  */

  const handleEditClick =
    async () => {
      if (changeDetail) {
        await onSubmit();

        return;
      }

      setChangeDetail(true);
    };

  /*
  |--------------------------------------------------------------------------
  | Annuler modification
  |--------------------------------------------------------------------------
  */

  const cancelEdit = () => {
    setFormData(
      (previousValue) => ({
        ...previousValue,

        name:
          auth.currentUser
            ?.displayName ||
          "",
      })
    );

    setChangeDetail(false);
  };

  /*
  |--------------------------------------------------------------------------
  | Demander suppression
  |--------------------------------------------------------------------------
  */

  const handleDeleteClick =
    () => {
      setShowDeleteConfirm(
        true
      );

      toast.info(
        t(
          "fill_reason_before_delete",
          "Vous pouvez indiquer la raison de votre départ."
        )
      );
    };

  /*
  |--------------------------------------------------------------------------
  | Annuler suppression
  |--------------------------------------------------------------------------
  */

  const cancelDelete = () => {
    setShowDeleteConfirm(
      false
    );

    setFormData(
      (previousValue) => ({
        ...previousValue,
        reason: "",
      })
    );
  };

  /*
  |--------------------------------------------------------------------------
  | Supprimer compte
  |--------------------------------------------------------------------------
  */

  const confirmDeleteAccount =
    async () => {
      const user =
        auth.currentUser;

      if (!user) {
        return;
      }

      const confirmed =
        window.confirm(
          t(
            "profile.final_delete_confirm",
            "Cette action est définitive. Voulez-vous vraiment supprimer votre compte ?"
          )
        );

      if (!confirmed) {
        return;
      }

      setDeleting(true);

      try {
        await deleteAccount();

        toast.success(
          t(
            "account_deleted",
            "Votre compte a été supprimé."
          )
        );

        navigate("/");
      } catch (error) {
        console.error(
          "Erreur suppression compte :",
          error
        );

        /*
          Firebase exige une connexion récente
          pour certaines opérations sensibles.
        */

        if (
          error?.status === 401 || error?.code === "auth/requires-recent-login"
        ) {
          toast.error(
            t(
              "profile.recent_login_required",
              "Pour des raisons de sécurité, reconnectez-vous avant de supprimer votre compte."
            )
          );

          navigate(
            "/SignIn",
            {
              state: {
                redirectTo:
                  "/profile",

                action:
                  "delete-account",
              },
            }
          );

          return;
        }

        toast.error(
          error?.message || t(
            "account_delete_error",
            "Impossible de supprimer votre compte."
          )
        );
      } finally {
        setDeleting(false);
      }
    };

  /*
  |--------------------------------------------------------------------------
  | Loading
  |--------------------------------------------------------------------------
  */

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-slate-50">
        <Spinner />
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-8 sm:px-6 lg:px-8 lg:py-12">
      <div className="mx-auto max-w-3xl">

        {/* Header */}

        <header className="mb-8 text-center">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-green-100 text-green-600">
            <LuUser
              size={26}
            />
          </span>

          <p className="mt-5 text-xs font-black uppercase tracking-[0.18em] text-green-600">
            Lina Immo
          </p>

          <h1 className="mt-2 text-3xl font-black text-slate-950 sm:text-4xl">
            {t(
              "my_profile",
              "Mon profil"
            )}
          </h1>

          <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-slate-500">
            {t(
              "profile.description",
              "Gérez vos informations personnelles et les paramètres de votre compte."
            )}
          </p>
        </header>

        {/* Profil */}

        <AdminLink />
        <section className="overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-xl shadow-slate-900/5">

          {/* En-tête carte */}

          <div className="border-b border-slate-100 px-5 py-5 sm:px-7">
            <div className="flex items-center gap-4">

              <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-slate-950 text-xl font-black text-white">
                {formData.name
                  ?.charAt(0)
                  ?.toUpperCase() ||
                  "U"}
              </span>

              <div className="min-w-0">
                <h2 className="truncate text-lg font-black text-slate-900">
                  {formData.name ||
                    t(
                      "profile.user",
                      "Utilisateur"
                    )}
                </h2>

                <p className="mt-1 flex items-center gap-1.5 truncate text-sm text-slate-500">
                  <LuMail
                    size={14}
                  />

                  {
                    formData.email
                  }
                </p>
              </div>
            </div>
          </div>

          {/* Informations */}

          <div className="p-5 sm:p-7">

            <div>
              <label
                htmlFor="name"
                className="mb-2 block text-sm font-black text-slate-700"
              >
                {t(
                  "profile.name",
                  "Nom"
                )}
              </label>

              <div className="relative">

                <LuUser
                  size={17}
                  className="pointer-events-none absolute start-4 top-1/2 -translate-y-1/2 text-slate-400"
                />

                <input
                  type="text"
                  id="name"
                  value={
                    formData.name
                  }
                  disabled={
                    !changeDetail ||
                    saving
                  }
                  onChange={
                    onChange
                  }
                  className={`
                    h-12
                    w-full
                    rounded-2xl
                    border
                    ps-11
                    pe-4
                    text-sm
                    font-semibold
                    outline-none
                    transition

                    ${
                      changeDetail
                        ? "border-green-300 bg-white text-slate-900 focus:border-green-500 focus:ring-4 focus:ring-green-500/10"
                        : "cursor-default border-slate-200 bg-slate-50 text-slate-600"
                    }
                  `}
                />
              </div>
            </div>

            {/* Email */}

            <div className="mt-5">
              <label
                className="mb-2 block text-sm font-black text-slate-700"
              >
                {t(
                  "profile.email",
                  "E-mail"
                )}
              </label>

              <div className="relative">

                <LuMail
                  size={17}
                  className="pointer-events-none absolute start-4 top-1/2 -translate-y-1/2 text-slate-400"
                />

                <input
                  type="email"
                  value={
                    formData.email
                  }
                  disabled
                  className="
                    h-12
                    w-full
                    cursor-not-allowed
                    rounded-2xl
                    border
                    border-slate-200
                    bg-slate-50
                    ps-11
                    pe-4
                    text-sm
                    font-medium
                    text-slate-500
                  "
                />
              </div>

              <p className="mt-2 text-xs text-slate-400">
                {t(
                  "profile.email_notice",
                  "L’adresse e-mail est liée à votre compte de connexion."
                )}
              </p>
            </div>

            {/* Actions profil */}

            <div className="mt-7 flex flex-col gap-3 sm:flex-row">

              <button
                type="button"
                onClick={
                  handleEditClick
                }
                disabled={saving}
                className="
                  inline-flex
                  h-11
                  flex-1
                  items-center
                  justify-center
                  gap-2
                  rounded-xl
                  bg-slate-950
                  px-4
                  text-sm
                  font-bold
                  text-white
                  transition
                  hover:bg-green-600
                  disabled:cursor-not-allowed
                  disabled:bg-slate-300
                "
              >
                {saving ? (
                  <>
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />

                    {t(
                      "profile.saving",
                      "Enregistrement..."
                    )}
                  </>
                ) : changeDetail ? (
                  <>
                    <LuSave
                      size={16}
                    />

                    {t(
                      "apply_changes",
                      "Enregistrer"
                    )}
                  </>
                ) : (
                  <>
                    <LuPencil
                      size={16}
                    />

                    {t(
                      "edit",
                      "Modifier"
                    )}
                  </>
                )}
              </button>

              {changeDetail && (
                <button
                  type="button"
                  onClick={
                    cancelEdit
                  }
                  disabled={saving}
                  className="
                    inline-flex
                    h-11
                    items-center
                    justify-center
                    gap-2
                    rounded-xl
                    border
                    border-slate-200
                    bg-white
                    px-5
                    text-sm
                    font-bold
                    text-slate-600
                    transition
                    hover:bg-slate-100
                  "
                >
                  <LuX
                    size={16}
                  />

                  {t(
                    "cancel",
                    "Annuler"
                  )}
                </button>
              )}
            </div>
          </div>
        </section>

        {/* Session */}

        <section className="mt-6 rounded-3xl border border-slate-100 bg-white p-5 shadow-sm sm:p-7">

          <h2 className="text-lg font-black text-slate-900">
            {t(
              "profile.session",
              "Session"
            )}
          </h2>

          <p className="mt-1 text-sm text-slate-500">
            {t(
              "profile.session_description",
              "Vous pouvez vous déconnecter de votre compte à tout moment."
            )}
          </p>

          <button
            type="button"
            onClick={onLogout}
            className="
              mt-5
              inline-flex
              h-11
              items-center
              justify-center
              gap-2
              rounded-xl
              border
              border-slate-200
              bg-white
              px-5
              text-sm
              font-bold
              text-slate-700
              transition
              hover:border-slate-300
              hover:bg-slate-100
            "
          >
            <LuLogOut
              size={16}
            />

            {t(
              "logout",
              "Se déconnecter"
            )}
          </button>
        </section>

        {/* Zone dangereuse */}

        <section id="delete-account" className="mt-6 rounded-3xl border border-red-200 bg-white p-5 shadow-sm sm:p-7">

          <div className="flex items-start gap-3">

            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-red-50 text-red-600">
              <LuShieldAlert
                size={20}
              />
            </span>

            <div>
              <h2 className="text-lg font-black text-slate-900">
                {t(
                  "profile.danger_zone",
                  "Zone sensible"
                )}
              </h2>

              <p className="mt-1 text-sm leading-6 text-slate-500">
                {t(
                  "profile.delete_description",
                  "La suppression de votre compte est définitive et ne peut pas être annulée."
                )}
              </p>
            </div>
          </div>

          {!showDeleteConfirm ? (
            <button
              type="button"
              onClick={
                handleDeleteClick
              }
              className="
                mt-5
                inline-flex
                h-11
                items-center
                justify-center
                gap-2
                rounded-xl
                border
                border-red-200
                bg-red-50
                px-5
                text-sm
                font-bold
                text-red-600
                transition
                hover:bg-red-100
              "
            >
              <LuTrash2
                size={16}
              />

              {t(
                "delete_account",
                "Supprimer mon compte"
              )}
            </button>
          ) : (
            <div className="mt-6 rounded-2xl border border-red-100 bg-red-50/50 p-4 sm:p-5">

              <h3 className="font-black text-red-700">
                {t(
                  "profile.delete_confirm_title",
                  "Confirmer la suppression"
                )}
              </h3>

              <p className="mt-2 text-sm leading-6 text-slate-600">
                {t(
                  "profile.delete_reason_text",
                  "Vous pouvez nous indiquer pourquoi vous souhaitez supprimer votre compte."
                )}
              </p>

              <textarea
                id="reason"
                value={
                  formData.reason
                }
                onChange={
                  onChange
                }
                rows={4}
                placeholder={t(
                  "delete_reason_placeholder",
                  "Raison de la suppression (facultatif)"
                )}
                className="
                  mt-4
                  min-h-[110px]
                  w-full
                  resize-y
                  rounded-2xl
                  border
                  border-slate-200
                  bg-white
                  px-4
                  py-3
                  text-sm
                  text-slate-900
                  outline-none
                  transition
                  placeholder:text-slate-400
                  focus:border-red-400
                  focus:ring-4
                  focus:ring-red-500/10
                "
              />

              <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">

                <button
                  type="button"
                  onClick={
                    cancelDelete
                  }
                  disabled={
                    deleting
                  }
                  className="
                    inline-flex
                    h-11
                    items-center
                    justify-center
                    gap-2
                    rounded-xl
                    border
                    border-slate-200
                    bg-white
                    px-5
                    text-sm
                    font-bold
                    text-slate-600
                    transition
                    hover:bg-slate-100
                  "
                >
                  <LuX
                    size={16}
                  />

                  {t(
                    "cancel",
                    "Annuler"
                  )}
                </button>

                <button
                  type="button"
                  onClick={
                    confirmDeleteAccount
                  }
                  disabled={
                    deleting
                  }
                  className="
                    inline-flex
                    h-11
                    items-center
                    justify-center
                    gap-2
                    rounded-xl
                    bg-red-600
                    px-5
                    text-sm
                    font-black
                    text-white
                    transition
                    hover:bg-red-700
                    disabled:cursor-not-allowed
                    disabled:bg-red-300
                  "
                >
                  {deleting ? (
                    <>
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />

                      {t(
                        "profile.deleting",
                        "Suppression..."
                      )}
                    </>
                  ) : (
                    <>
                      <LuTrash2
                        size={16}
                      />

                      {t(
                        "confirm_delete_account",
                        "Supprimer définitivement"
                      )}
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </section>
      </div>
    </main>
  );
}